import styled from '@emotion/styled';
import { useAtomValue } from 'jotai';
import { t } from 'i18next';
import ExclamationIcon from '@/public/icons/exclamation_circle.svg';
import RefreshIcon from '@/public/icons/refresh.svg';
import XIcon from '@/public/icons/record/x.svg';
import { queuedRecordNoticeAtom } from '@/components/Record/TalkFLow/hooks/useQueuedRecordRecovery';

/**
 * 「음성 파일」 칸의 업로드 실패 안내 (PORTAL-12583 · Figma Field @ Focus Out - Fail).
 * 문서에 들어올 때마다 띄우던 복구 안내창을 대신한다.
 * ⟳ 다시 올리기 · ✕ 이번 방문 동안 숨기기 — 숨겨도 백업은 남아 다음 진입 때 다시 보인다.
 */
const UploadFailedNotice = ({ docId }: { docId: string }) => {
  const notice = useAtomValue(queuedRecordNoticeAtom);
  if (!notice || notice.docId !== docId) return null;
  const { count, days, retrying, retry, dismiss } = notice;

  return (
    <Notice role="alert" title={t('{{days}}일 뒤 자동 삭제돼요', { days })}>
      <IconCircle>
        <ExclamationIcon width={12} height={12} />
      </IconCircle>
      <Message>
        {count > 1
          ? t('파일 {{n}}개 업로드에 실패했습니다. 다시 시도해 주세요.', {
              n: count,
            })
          : t('파일 업로드에 실패했습니다. 다시 시도해 주세요.')}
      </Message>
      <IconButton
        type="button"
        aria-label={t('다시 시도')}
        onClick={retry}
        disabled={retrying}
        spinning={retrying}
      >
        <RefreshIcon width={16} height={16} />
      </IconButton>
      <Divider />
      <IconButton
        type="button"
        aria-label={t('닫기')}
        onClick={dismiss}
        disabled={retrying}
      >
        <XIcon width={10} height={10} />
      </IconButton>
    </Notice>
  );
};

export default UploadFailedNotice;

const Notice = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  max-width: 100%;
  margin-bottom: 8px;
  padding: 4px 8px 4px 4px;
  border: 1px solid #e7e9ec;
  border-radius: 4px;
  background-color: #fff;
`;

const IconCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: rgba(245, 80, 96, 0.12);
`;

const Message = styled.span`
  font-size: 12px;
  line-height: 18px;
  color: #f55060;
  word-break: keep-all;
`;

const Divider = styled.div`
  flex: none;
  width: 1px;
  height: 12px;
  background-color: #e7e9ec;
`;

const IconButton = styled.button<{ spinning?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 16px;
  height: 16px;
  padding: 0;
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }

  svg path {
    stroke: #888d96;
  }

  ${({ spinning }) =>
    spinning
      ? `
    animation: upload-failed-spin 1s linear infinite;
    @keyframes upload-failed-spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `
      : ''}
`;
